import { useStoreActions, useStoreState } from '@discover-data/stores/restaurant/hooks';
import usePage from '@discover-data/stores/hooks/page/usePage';
import useTagIdsQueryState from './useQueryState';
import getTagOptions from './getOptions';
import isValidTagId from './isValid';

export default function useTagIds(
  tags: {
    category: string | null;
    title: string | null;
    id: number | null;
  }[]
): [number[], (newTagIds: number[]) => Promise<void>] {
  const { optionIds } = getTagOptions(tags);

  const storeMode = useStoreState((state) => state.mode);
  const storeValues = useStoreState((state) => state.tagIds.value);

  // The `tags` could be a subset of the complete set of tags, so we sort the store values into relevant and irrelevant groups. That way we can easily manipulate the relevant store values while preserving the irrelevant ones.
  const sortedStoreValues = storeValues.reduce(
    (
      {
        relevantStoreValues,
        irrelevantStoreValues,
      }: {
        relevantStoreValues: number[];
        irrelevantStoreValues: number[];
      },
      id
    ) => {
      if (isValidTagId(id, optionIds)) {
        relevantStoreValues.push(id);
      } else {
        irrelevantStoreValues.push(id);
      }

      return {
        relevantStoreValues,
        irrelevantStoreValues,
      };
    },
    {
      relevantStoreValues: [],
      irrelevantStoreValues: [],
    }
  );

  const setStoreValues = useStoreActions((actions) => actions.tagIds.set);
  const [, setRouterValues] = useTagIdsQueryState(tags);
  const { resetPage } = usePage();

  async function set(newValues: number[]) {
    resetPage();

    const validNewValues = newValues.filter((id) => isValidTagId(id, optionIds));

    setStoreValues([...sortedStoreValues.irrelevantStoreValues, ...validNewValues]);

    await setRouterValues(validNewValues);
  }

  return [sortedStoreValues.relevantStoreValues, set];
}
